import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from "react-helmet-async";
import { message } from 'antd';
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import Breadcrumb from '../../components/Common/Breadcrumb';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import FeatherIcon from 'feather-icons-react';
import { useNavigate } from 'react-router-dom';
import { createCarousel } from "../../services/carouselService";
import { jwtDecode } from "jwt-decode";
import { fetchUserById } from "../../services/userService";

const AddCarousel = () => {
  const navigate = useNavigate();
  const [slideTitle, setSlideTitle] = useState("");
  const [slideDescription, setSlideDescription] = useState("");
  const [redirectUrl, setRedirectUrl] = useState("");
  const [buttonPlaceholder, setButtonPlaceholder] = useState("");
  const [isFeatured, setIsFeatured] = useState(false);
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loggedInUserData, setLoggedInUserData] = useState(null);

  const fileInputRef = useRef(null);

  const breadcrumbItems = [
    { label: "Carousel" },
    { label: "Add List" },
  ];

  // ✅ Fetch logged-in user (to get email)
  useEffect(() => {
    const fetchAndSetUser = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const decoded = jwtDecode(token);
          const user = await fetchUserById(decoded.id);
          setLoggedInUserData(user);
        } catch (error) {
          console.error("Error decoding token or fetching user:", error);
          localStorage.removeItem("token");
          setLoggedInUserData(null);
        }
      }
    };

    fetchAndSetUser();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!slideTitle || !slideDescription || !redirectUrl || !buttonPlaceholder || !image) {
        throw new Error("Please fill all required fields");
      }

      const formData = new FormData();
      formData.append("carousel_title", slideTitle);
      formData.append("carousel_description", slideDescription);
      formData.append("carousel_url", redirectUrl);
      formData.append("carousel_button_placeholder", buttonPlaceholder);
      formData.append("carousel_isFeatured", isFeatured ? 1 : 0);
      formData.append("carousel_image", image);

      // ✅ Add addedbyemail
      if (loggedInUserData?.user_email) {
        formData.append("addedbyemail", loggedInUserData.user_email);
      }

      await createCarousel(formData);
      message.success("Carousel slide added successfully!");
      navigate('/list-carousel');

      // Reset form
      setSlideTitle("");
      setSlideDescription("");
      setRedirectUrl("");
      setButtonPlaceholder("");
      setIsFeatured(false);
      setImage(null);
      if (fileInputRef.current) fileInputRef.current.value = null;

    } catch (err) {
      message.error(err.message || "Failed to submit");
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        message.error("Image size should be less than 2MB");
        return;
      }
      setImage(file);
      message.success('Image selected successfully');
    }
  };

  const handleBrowseClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  if (loading) {
    return (
      <div>
        <Helmet><title>Add Carousel | CRM Jnec</title></Helmet>
        <Header />    
        <Sidebar activeClassName='add-carousel' />
        <div className="page-wrapper">
          <div className="content">
            <LoadingSpinner message="Adding carousel slide..." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Helmet><title>Add Carousel | CRM Jnec</title></Helmet>
      <Header />    
      <Sidebar activeClassName='add-carousel' />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12"><Breadcrumb items={breadcrumbItems} /></div>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      {/* Form Section */}
                      <div className="col-md-8">
                        <div className="p-3 border rounded bg-white">
                          <div className="row">
                            <div className="col-12">
                              <div className="form-group local-forms">
                                <label>Slide title <span className="text-danger">*</span></label>
                                <input
                                  className="form-control"
                                  type="text"
                                  placeholder="Enter title. Max characters: 255 characters"
                                  value={slideTitle}
                                  onChange={(e) => setSlideTitle(e.target.value)}
                                  maxLength={255}
                                  required
                                />
                              </div>
                            </div>

                            <div className="col-12 mt-3">
                              <div className="form-group local-forms">
                                <label>Slide Description <span className="text-danger">*</span></label>
                                <textarea
                                  className="form-control"
                                  placeholder="Enter description. Max characters: 500 characters"
                                  value={slideDescription}
                                  onChange={(e) => setSlideDescription(e.target.value)}
                                  maxLength={500}
                                  rows={4}
                                  required
                                />
                              </div>
                            </div>

                            <div className="col-12 mt-3">
                              <div className="form-group local-forms">
                                <label>Redirect URL <span className="text-danger">*</span></label>
                                <input
                                  className="form-control"
                                  type="url"
                                  placeholder="Enter redirect url"
                                  value={redirectUrl}
                                  onChange={(e) => setRedirectUrl(e.target.value)}
                                  required
                                />
                              </div>
                            </div>

                            <div className="col-12 mt-3">
                              <div className="form-group local-forms">
                                <label>Button Placeholder <span className="text-danger">*</span></label>
                                <input
                                  className="form-control"
                                  type="text"
                                  placeholder="Enter Value. Eg: Apply Now"
                                  value={buttonPlaceholder}
                                  onChange={(e) => setButtonPlaceholder(e.target.value)}
                                  required
                                />
                              </div>
                            </div>

                            <div className="col-12 mt-3">
                              <div className="form-check">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id="isFeatured"
                                  checked={isFeatured}
                                  onChange={(e) => setIsFeatured(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="isFeatured">
                                  Is Featured
                                </label>
                                <small className="d-block text-muted">
                                  Enabling this will make this slide appear first in the sequence
                                </small>
                              </div>
                            </div>

                            <div className="col-12 mt-3">
                              <div className="form-group">
                                <label>Select Slide Image <span className="text-danger">*</span></label>
                                <div className="upload-box border rounded p-3 text-center">
                                  <input
                                    type="file"
                                    ref={fileInputRef}
                                    className="d-none"
                                    accept=".png,.jpg,.jpeg,.gif,.webp"
                                    onChange={handleImageChange}
                                    required
                                  />
                                  <div className="mb-2">
                                    <i className="fas fa-cloud-upload-alt fa-3x text-muted"></i>
                                  </div>
                                  <div className="mb-2">Drag & Drop</div>
                                  <div className="text-muted small">
                                    Format: .png, .jpg, .jpeg, .gif, .webp<br />
                                    Max Size: 2MB
                                  </div>
                                  <div className="mt-2">
                                    <button
                                      type="button"
                                      className="btn btn-primary btn-sm"
                                      onClick={handleBrowseClick}
                                    >
                                      Browse Files
                                    </button>
                                  </div>
                                </div>
                                {image && (
                                  <div className="mt-2">
                                    <small>Selected: {image.name}</small>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="col-12 mt-4 text-end">
                              <button 
                                type="submit" 
                                className="btn btn-primary"
                                disabled={loading}
                              >
                                {loading ? 'Submitting...' : 'Submit'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Instruction Section */}
                      <div className="col-md-4">
                        <div className="p-3 border rounded bg-light">
                          <h5 className="mb-3">Instructions</h5>
                          <ul className="list-unstyled small">
                            <li className="mb-2">• Keep slide titles concise and descriptive.</li>
                            <li className="mb-2">• Descriptions should be engaging but brief.</li>
                            <li className="mb-2">• Use high-quality images for best results.</li>
                            <li className="mb-2">• Recommended image dimensions: 1920x1080px.</li>
                            <li className="mb-2">• Max Image Size: 2MB</li>
                            <li className="mb-2">• Featured slides will appear first in the carousel.</li>
                            <li className="mb-2">• Button placeholder text should be action-oriented.</li>
                            <li className="mb-2">• Recommended text: &quot;Apply Now&quot;, &quot;Know More&quot;, etc.</li>
                            <li className="mb-2">• Compress images using:
                              <ul>
                                <li><a href="https://tinypng.com/" target="_blank" rel="noopener noreferrer"><FeatherIcon icon="external-link" size={14} className="ms-1"/> TinyPNG</a></li>
                                <li><a href="https://compressor.io/" target="_blank" rel="noopener noreferrer"><FeatherIcon icon="external-link" size={14} className="ms-1"/> Compressor.io</a></li>
                                <li><a href="https://squoosh.app/" target="_blank" rel="noopener noreferrer"><FeatherIcon icon="external-link" size={14} className="ms-1"/> Squoosh</a></li>
                              </ul>
                            </li>
                          </ul>
                        </div>
                      </div>

                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCarousel;